/**
 * Sidebar — GCP-style collapsible left nav.
 *
 * Structure:
 *   - Brand header (logo + Trackerity)
 *   - Pinned favorites strip (6 hardcoded links)
 *   - SidebarSearch (filters tree live)
 *   - Scrollable tree (14 sections → subsections → optional sub-sub-sections)
 *   - Collapse-to-64px toggle (persisted in localStorage)
 *
 * Design rules enforced:
 *   - Brand GREEN only (active bg-green-50 / text-green-700 / border-l-green-600)
 *   - Inline SVG icons (no external libs)
 *   - aria-current / role=tree / aria-expanded
 *   - Kbd: up/down navigate, left collapse, right expand, enter navigate, / focus search
 */
import {
	Link,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	pinnedFavorites,
	sidebarTree,
	allLeaves,
	type SidebarLeaf,
	type SidebarNode,
} from "../../lib/sidebar-tree";
import { Chevron, Star } from "./icons";
import { SidebarItem } from "./SidebarItem";
import { SidebarSearch } from "./SidebarSearch";
import { SidebarSection } from "./SidebarSection";

const COLLAPSE_STORAGE_KEY = "trackerity.sidebar.collapsed";
const OPEN_PATHS_STORAGE_KEY = "trackerity.sidebar.openPaths";

function readCollapsed(): boolean {
	try {
		return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "1";
	} catch {
		return false;
	}
}

function readOpenPaths(): Set<string> {
	try {
		const raw = localStorage.getItem(OPEN_PATHS_STORAGE_KEY);
		if (!raw) return new Set();
		const arr = JSON.parse(raw);
		if (Array.isArray(arr)) return new Set(arr);
	} catch {
		// ignore
	}
	return new Set();
}

/** Does this node's subtree contain an href equal to activeHref? */
function subtreeHasActive(node: SidebarNode, activeHref: string): boolean {
	if (node.leaves?.some((l) => l.href === activeHref)) return true;
	if (!node.children) return false;
	return node.children.some((c) => subtreeHasActive(c, activeHref));
}

export function Sidebar() {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const activeHref = pathname || "/";
	const navigate = useNavigate();

	const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
	const [openPaths, setOpenPaths] = useState<Set<string>>(readOpenPaths);
	const [query, setQuery] = useState<string>("");
	const searchRef = useRef<HTMLInputElement | null>(null);
	const navRef = useRef<HTMLDivElement | null>(null);

	// Persist collapsed state.
	useEffect(() => {
		try {
			localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? "1" : "0");
		} catch {
			// ignore
		}
	}, [collapsed]);

	// Persist open paths.
	useEffect(() => {
		try {
			localStorage.setItem(
				OPEN_PATHS_STORAGE_KEY,
				JSON.stringify([...openPaths]),
			);
		} catch {
			// ignore
		}
	}, [openPaths]);

	// Auto-open the section that contains the active route on first mount.
	useEffect(() => {
		if (!activeHref) return;
		setOpenPaths((prev) => {
			const next = new Set(prev);
			for (const sec of sidebarTree) {
				if (subtreeHasActive(sec, activeHref)) {
					next.add(sec.label);
					for (const sub of sec.children ?? []) {
						if (subtreeHasActive(sub, activeHref)) {
							next.add(`${sec.label}/${sub.label}`);
							for (const ssub of sub.children ?? []) {
								if (subtreeHasActive(ssub, activeHref)) {
									next.add(
										`${sec.label}/${sub.label}/${ssub.label}`,
									);
								}
							}
						}
					}
				}
			}
			return next;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleCollapse = useCallback(() => {
		setCollapsed((c) => !c);
	}, []);

	const toggleOpen = useCallback((path: string) => {
		setOpenPaths((prev) => {
			const next = new Set(prev);
			if (next.has(path)) next.delete(path);
			else next.add(path);
			return next;
		});
	}, []);

	// Flat filter for search mode.
	const filteredLeaves = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return null;
		return allLeaves.filter(
			(l) =>
				l.label.toLowerCase().includes(q) ||
				l.slug.toLowerCase().includes(q),
		);
	}, [query]);

	// Keyboard navigation.
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			// Ignore if user is typing in search.
			if (document.activeElement === searchRef.current) return;
			const container = navRef.current;
			if (!container) return;
			const items = Array.from(
				container.querySelectorAll<HTMLElement>(
					"[role=treeitem]",
				),
			);
			const current = document.activeElement as HTMLElement | null;
			const idx = current ? items.indexOf(current) : -1;

			if (e.key === "ArrowDown") {
				e.preventDefault();
				const next = items[Math.min(items.length - 1, Math.max(0, idx + 1))];
				next?.focus();
			} else if (e.key === "ArrowUp") {
				e.preventDefault();
				const next = items[Math.max(0, idx - 1)];
				next?.focus();
			} else if (e.key === "ArrowRight" && current) {
				// Expand the current section/subsection.
				if (current.getAttribute("aria-expanded") === "false") {
					current.click();
				}
			} else if (e.key === "ArrowLeft" && current) {
				if (current.getAttribute("aria-expanded") === "true") {
					current.click();
				}
			} else if (e.key === "Enter" && current) {
				const href = current.getAttribute("href");
				if (href) {
					e.preventDefault();
					navigate({ to: href });
				} else {
					current.click();
				}
			} else if (e.key === "/") {
				e.preventDefault();
				searchRef.current?.focus();
			}
		},
		[navigate],
	);

	// Collapsed view: only icons.
	if (collapsed) {
		return (
			<aside
				className="w-[64px] shrink-0 border-r border-zinc-200 bg-white flex flex-col"
				aria-label="Sidebar (collapsed)"
			>
				<div className="px-3 pt-4 pb-3 border-b border-zinc-100 flex justify-center">
					<button
						type="button"
						onClick={toggleCollapse}
						className="w-8 h-8 rounded-md bg-green-600 text-white font-semibold flex items-center justify-center text-sm"
						aria-label="Expand sidebar"
						title="Expand sidebar"
					>
						T
					</button>
				</div>
				<ul
					role="tree"
					aria-label="Trackerity navigation (collapsed)"
					className="flex-1 overflow-y-auto px-2 py-2 flex flex-col gap-1 items-center"
				>
					{sidebarTree.map((sec) => {
						const hasActive = subtreeHasActive(sec, activeHref);
						return (
							<SidebarSection
								key={sec.label}
								node={sec}
								open={false}
								onToggle={toggleCollapse}
								activeHref={activeHref}
								collapsed
								openPaths={openPaths}
								toggleOpen={toggleOpen}
								hasActive={hasActive}
							/>
						);
					})}
				</ul>
			</aside>
		);
	}

	return (
		<aside
			className="w-[260px] shrink-0 border-r border-zinc-200 bg-white flex flex-col"
			aria-label="Sidebar"
		>
			{/* Brand + collapse */}
			<div className="px-4 pt-4 pb-3 border-b border-zinc-100">
				<div className="flex items-center gap-2">
					<div className="w-7 h-7 rounded-md bg-green-600 text-white font-semibold flex items-center justify-center text-sm">
						T
					</div>
					<span className="text-[15px] font-semibold text-zinc-900 flex-1 truncate">
						Trackerity
					</span>
					<button
						type="button"
						onClick={toggleCollapse}
						className="text-zinc-400 hover:text-zinc-700 p-1 rounded"
						aria-label="Collapse sidebar"
						title="Collapse sidebar"
					>
						<Chevron className="w-4 h-4 rotate-180" />
					</button>
				</div>
				<div className="mt-3">
					<SidebarSearch
						ref={searchRef}
						query={query}
						onChange={setQuery}
					/>
				</div>
			</div>

			{/* Pinned favorites */}
			{query.trim() ? null : (
				<div className="px-3 pt-3">
					<div className="text-[10px] tracking-wider uppercase text-zinc-400 font-semibold px-1 mb-1 flex items-center gap-1">
						<Star className="w-3 h-3" />
						<span>Pinned</span>
					</div>
					<ul className="flex flex-col gap-0.5">
						{pinnedFavorites.map((p) => {
							const active = p.href === activeHref;
							return (
								<li key={p.slug}>
									<Link
										to={p.href}
										aria-current={active ? "page" : undefined}
										className={
											active
												? "flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-green-700 bg-green-50 font-medium"
												: "flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-zinc-700 hover:bg-zinc-50"
										}
									>
										<Star
											className={
												active
													? "w-3.5 h-3.5 text-green-600"
													: "w-3.5 h-3.5 text-zinc-400"
											}
										/>
										<span className="truncate">{p.label}</span>
									</Link>
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{/* Tree */}
			<div
				ref={navRef}
				onKeyDown={onKeyDown}
				className="flex-1 overflow-y-auto px-2 py-2"
				aria-label="Sidebar navigation"
			>
				{filteredLeaves ? (
					<div>
						<div className="text-[11px] text-zinc-500 px-2 py-1">
							{filteredLeaves.length} matches
						</div>
						<ul role="tree" className="flex flex-col">
							{filteredLeaves.length === 0 ? (
								<li className="text-[12px] text-zinc-400 px-3 py-2">
									No features match "{query}"
								</li>
							) : (
								filteredLeaves.slice(0, 200).map((leaf, i) => (
									<SidebarItem
										key={`${leaf.slug}|${leaf.href}|${i}`}
										leaf={leaf as SidebarLeaf}
										depth={0}
										active={leaf.href === activeHref}
									/>
								))
							)}
						</ul>
						{filteredLeaves.length > 200 ? (
							<div className="text-[11px] text-zinc-400 px-3 py-2">
								Showing first 200 matches — refine your query for more.
							</div>
						) : null}
					</div>
				) : (
					<ul
						role="tree"
						aria-label="Trackerity navigation"
						className="flex flex-col gap-0.5"
					>
						{sidebarTree.map((sec) => {
							const hasActive = subtreeHasActive(sec, activeHref);
							const open =
								openPaths.has(sec.label) ||
								(hasActive && !openPaths.has(`!${sec.label}`));
							return (
								<SidebarSection
									key={sec.label}
									node={sec}
									open={open}
									onToggle={() => toggleOpen(sec.label)}
									activeHref={activeHref}
									collapsed={false}
									openPaths={openPaths}
									toggleOpen={toggleOpen}
									hasActive={hasActive}
								/>
							);
						})}
					</ul>
				)}
			</div>

			{/* Footer */}
			<div className="px-3 py-2 border-t border-zinc-100">
				<span className="inline-block bg-zinc-100 text-zinc-700 text-[11px] px-2 py-1 rounded">
					Local dev
				</span>
			</div>
		</aside>
	);
}
